import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { formatDateForDisplay, getCurrentDateForInput } from '../utils/dateFormat';
import { trackFeatureUsage, trackDownload, trackEvent } from '../utils/analytics';

const AdminSubscriptionInvoices = () => {
  const { user } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: getCurrentDateForInput()
  });
  const [searchLoading, setSearchLoading] = useState(false);
  const [totalAmount, setTotalAmount] = useState(0);
  const [totalInvoices, setTotalInvoices] = useState(0);
  const [showGenerateInvoiceModal, setShowGenerateInvoiceModal] = useState(false);
  const [invoiceUserSearch, setInvoiceUserSearch] = useState('');
  const [invoiceUserResults, setInvoiceUserResults] = useState([]);
  const [invoiceUserSearchLoading, setInvoiceUserSearchLoading] = useState(false);
  const [selectedInvoiceUser, setSelectedInvoiceUser] = useState(null);
  const [invoiceGenerationLoading, setInvoiceGenerationLoading] = useState(false);
  const [invoiceGenerationError, setInvoiceGenerationError] = useState('');
  const [invoiceMonth, setInvoiceMonth] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    // Set default start date to 30 days ago
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    setDateRange(prev => ({
      ...prev,
      startDate: thirtyDaysAgo.toISOString().split('T')[0]
    }));
    
    trackFeatureUsage('Admin Subscription Invoices', 'view');
  }, []);

  useEffect(() => {
    if (dateRange.startDate && dateRange.endDate) {
      fetchSubscriptionInvoices();
    }
  }, [dateRange]);

  useEffect(() => {
    if (!showGenerateInvoiceModal || selectedInvoiceUser) return undefined;

    const query = invoiceUserSearch.trim();
    if (query.length < 2) {
      setInvoiceUserResults([]);
      setInvoiceUserSearchLoading(false);
      return undefined;
    }

    const timeoutId = setTimeout(async () => {
      setInvoiceUserSearchLoading(true);
      setInvoiceGenerationError('');
      try {
        const response = await axios.get('/api/admin/subscription-invoices/users', {
          params: { query }
        });
        setInvoiceUserResults(response.data || []);
      } catch (searchError) {
        console.error('Failed to search subscription users:', searchError);
        setInvoiceUserResults([]);
        setInvoiceGenerationError('Unable to search users right now. Please try again.');
      } finally {
        setInvoiceUserSearchLoading(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [invoiceUserSearch, selectedInvoiceUser, showGenerateInvoiceModal]);

  const fetchSubscriptionInvoices = async () => {
    if (!dateRange.startDate || !dateRange.endDate) {
      return;
    }

    try {
      setSearchLoading(true);
      
      const params = {
        startDate: dateRange.startDate,
        endDate: dateRange.endDate
      };
      
      const response = await axios.get('/api/admin/subscription-invoices', { params });
      const data = response.data;
      
      setInvoices(data);
      
      // Calculate totals
      const total = data.reduce((sum, invoice) => sum + (parseFloat(invoice.totalAmount) || 0), 0);
      setTotalAmount(total);
      setTotalInvoices(data.length);
      
      trackEvent('admin_subscription_invoices_filtered', {
        event_category: 'Admin Management',
        event_label: 'Subscription Invoices Filtered',
        date_range: `${dateRange.startDate} to ${dateRange.endDate}`,
        total_invoices: data.length,
        total_amount: total
      });
    } catch (error) {
      console.error('Failed to fetch subscription invoices:', error);
      setError('Failed to load subscription invoices. Please try again.');
    } finally {
      setLoading(false);
      setSearchLoading(false);
    }
  };

  const handleDownloadInvoice = async (invoiceId, fileName) => {
    try {
      trackDownload(fileName, 'pdf');
      trackEvent('admin_subscription_invoice_downloaded', {
        event_category: 'Admin Management',
        event_label: fileName,
        invoice_id: invoiceId
      });

      const response = await axios.get(`/api/admin/subscription-invoices/${invoiceId}/download`, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download subscription invoice:', error);
      setError('Failed to download subscription invoice. Please try again.');
    }
  };

  const openGenerateInvoiceModal = () => {
    const today = new Date();
    setInvoiceMonth(`${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`);
    setShowGenerateInvoiceModal(true);
    setInvoiceUserSearch('');
    setInvoiceUserResults([]);
    setSelectedInvoiceUser(null);
    setInvoiceGenerationError('');
    setSuccessMessage('');
  };

  const closeGenerateInvoiceModal = () => {
    if (invoiceGenerationLoading) return;
    setShowGenerateInvoiceModal(false);
    setInvoiceUserSearch('');
    setInvoiceUserResults([]);
    setInvoiceUserSearchLoading(false);
    setSelectedInvoiceUser(null);
    setInvoiceGenerationError('');
  };

  const selectInvoiceUser = (selectedUser) => {
    setSelectedInvoiceUser(selectedUser);
    setInvoiceUserSearch(selectedUser.fullName);
    setInvoiceUserResults([]);
    setInvoiceGenerationError('');
  };

  const generateMissingInvoice = async () => {
    if (!selectedInvoiceUser?.id) return;
    if (!/^[0-9]{4}-(0[1-9]|1[0-2])$/.test(invoiceMonth) || invoiceMonth.startsWith('0000')) {
      setInvoiceGenerationError('Select a valid payment month.');
      return;
    }

    setInvoiceGenerationLoading(true);
    setInvoiceGenerationError('');
    try {
      const response = await axios.post('/api/admin/subscription-invoices/generate', null, {
        params: { userId: selectedInvoiceUser.id, month: invoiceMonth }
      });
      const generatedInvoice = response.data;
      setSuccessMessage(`Invoice ${generatedInvoice.invoiceNumber} generated successfully for ${selectedInvoiceUser.fullName} for ${invoiceMonth}.`);
      setShowGenerateInvoiceModal(false);
      setSelectedInvoiceUser(null);
      setInvoiceUserSearch('');
      setInvoiceUserResults([]);
      await fetchSubscriptionInvoices();
      trackEvent('admin_subscription_invoice_generated', {
        event_category: 'Admin Management',
        event_label: generatedInvoice.invoiceNumber,
        invoice_id: generatedInvoice.id,
        customer_id: generatedInvoice.customerId
      });
    } catch (generationError) {
      console.error('Failed to manually generate subscription invoice:', generationError);
      setInvoiceGenerationError(
        generationError.response?.data?.message || 'Failed to generate the subscription invoice. Please try again.'
      );
    } finally {
      setInvoiceGenerationLoading(false);
    }
  };

  const getInvoiceStatusColor = (status) => {
    switch (status) {
      case 'PAID': return 'var(--success)';
      case 'PENDING': return 'var(--warning)';
      case 'OVERDUE': return 'var(--error)';
      case 'CANCELLED': return 'var(--gray-dark)';
      default: return 'var(--gray-dark)';
    }
  };

  const escapeCsvValue = (value) => {
    const stringValue = value === null || value === undefined ? '' : String(value);
    if (/[",\n]/.test(stringValue)) {
      return `"${stringValue.replace(/"/g, '""')}"`;
    }
    return stringValue;
  };

  const formatInvoiceNumberForExport = (invoiceNumber) => {
    if (!invoiceNumber) {
      return '';
    }

    const parts = String(invoiceNumber).split('-');
    if (/^\d{4}$/.test(parts[0])) {
      parts[0] = parts[0].slice(2);
      return parts.join('');
    }

    return String(invoiceNumber).replace(/-/g, '');
  };

  const exportToCSV = () => {
    if (invoices.length === 0) {
      setError('No data to export');
      return;
    }

    const headers = [
      'Invoice Number',
      'Customer Name',
      'GST Number',
      'Customer Email',
      'Invoice Date',
      'Amount',
      'Status',
      'Payment Date',
      'Payment Mode'
    ];

    const csvContent = [
      headers.join(','),
      ...invoices.map(invoice => [
        formatInvoiceNumberForExport(invoice.invoiceNumber),
        escapeCsvValue(invoice.customerName),
        escapeCsvValue(invoice.customerGstNumber || 'B2C'),
        escapeCsvValue(invoice.customerEmail),
        invoice.invoiceDate,
        invoice.totalAmount || invoice.amount,
        invoice.status,
        invoice.paymentDate || '',
        invoice.paymentMode || ''
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `subscription_invoices_${dateRange.startDate}_to_${dateRange.endDate}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    trackEvent('admin_subscription_invoices_exported', {
      event_category: 'Admin Management',
      event_label: 'Subscription Invoices CSV Export',
      records_count: invoices.length,
      total_amount: totalAmount
    });
  };

  if (loading && !dateRange.startDate) {
    return (
      <div className="page">
        <div className="loading">Loading subscription invoices...</div>
      </div>
    );
  }

  return (
    <div className="page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', flexWrap: 'wrap', marginBottom: '30px' }}>
        <h1 className="page-title" style={{ margin: 0 }}>Subscription Invoices Management</h1>
        {user?.systemRoles?.some(role => role.code === 'SUPER_ADMIN') && (
          <button type="button" className="btn btn-primary" onClick={openGenerateInvoiceModal}>
            Generate Invoice
          </button>
        )}
      </div>
      
      <div style={{
        marginBottom: '20px',
        padding: '15px',
        background: 'linear-gradient(135deg, #4A90E2 0%, #2C5282 100%)',
        color: 'white',
        borderRadius: '8px',
        textAlign: 'center'
      }}>
        <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '5px' }}>
          🔑 Super Administrator Access
        </div>
        <div style={{ fontSize: '14px', opacity: '0.9' }}>
          View and manage all platform subscription invoices across the system
        </div>
      </div>

      {error && (
        <div className="alert alert-error">{error}</div>
      )}

      {successMessage && (
        <div className="alert alert-success">{successMessage}</div>
      )}

      {/* Date Range Filter */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <h3 style={{ color: 'var(--dark-blue)', marginBottom: '20px' }}>
          Filter Subscription Invoices
        </h3>
        
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '15px', alignItems: 'end' }}>
          <div className="form-group" style={{ margin: 0 }}>
            <label htmlFor="startDate" className="form-label">
              Start Date *
            </label>
            <input
              type="date"
              id="startDate"
              className="form-input"
              value={dateRange.startDate}
              onChange={(e) => setDateRange({...dateRange, startDate: e.target.value})}
              required
              disabled={searchLoading}
            />
          </div>

          <div className="form-group" style={{ margin: 0 }}>
            <label htmlFor="endDate" className="form-label">
              End Date *
            </label>
            <input
              type="date"
              id="endDate"
              className="form-input"
              value={dateRange.endDate}
              onChange={(e) => setDateRange({...dateRange, endDate: e.target.value})}
              required
              disabled={searchLoading}
            />
          </div>

          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              className="btn btn-primary"
              onClick={fetchSubscriptionInvoices}
              disabled={searchLoading || !dateRange.startDate || !dateRange.endDate}
            >
              {searchLoading ? 'Loading...' : 'Filter'}
            </button>
            
            {invoices.length > 0 && (
              <button
                className="btn btn-secondary"
                onClick={exportToCSV}
                disabled={searchLoading}
                style={{ 
                  background: 'linear-gradient(135deg, #48BB78 0%, #38A169 100%)',
                  color: 'white',
                  border: 'none'
                }}
              >
                📊 Export CSV
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      {invoices.length > 0 && (
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
          gap: '20px',
          marginBottom: '20px'
        }}>
          <div className="stat-card" style={{ margin: 0 }}>
            <div className="stat-number">{totalInvoices}</div>
            <div className="stat-label">Total Invoices</div>
          </div>
          
          <div className="stat-card" style={{ margin: 0 }}>
            <div className="stat-number" style={{ color: 'var(--success)' }}>
              ₹{totalAmount.toFixed(2)}
            </div>
            <div className="stat-label">Total Revenue</div>
          </div>
          
          <div className="stat-card" style={{ margin: 0 }}>
            <div className="stat-number" style={{ color: 'var(--primary-blue)' }}>
              ₹{totalInvoices > 0 ? (totalAmount / totalInvoices).toFixed(2) : '0.00'}
            </div>
            <div className="stat-label">Average Invoice</div>
          </div>
          
          <div className="stat-card" style={{ margin: 0 }}>
            <div className="stat-number" style={{ color: 'var(--warning)' }}>
              {invoices.filter(inv => inv.status === 'PAID').length}
            </div>
            <div className="stat-label">Paid Invoices</div>
          </div>
        </div>
      )}

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 style={{ color: 'var(--dark-blue)', margin: 0 }}>
            Subscription Invoices ({invoices.length} found)
          </h3>
          {dateRange.startDate && dateRange.endDate && (
            <div style={{ fontSize: '14px', color: 'var(--gray-dark)' }}>
              Period: {formatDateForDisplay(dateRange.startDate)} to {formatDateForDisplay(dateRange.endDate)}
            </div>
          )}
        </div>

        {searchLoading ? (
          <div className="loading">Loading subscription invoices...</div>
        ) : invoices.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: 'var(--gray-dark)' }}>
            {dateRange.startDate && dateRange.endDate 
              ? 'No subscription invoices found for the selected date range.'
              : 'Please select a date range to view subscription invoices.'
            }
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ 
              width: '100%', 
              borderCollapse: 'collapse',
              fontSize: '14px'
            }}>
              <thead>
                <tr style={{ backgroundColor: 'var(--pale-blue)' }}>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>
                    Invoice #
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>
                    Customer
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>
                    GST Number
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>
                    Invoice Date
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>
                    Description
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>
                    Amount
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>
                    Status
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>
                    Payment Info
                  </th>
                  <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid var(--light-blue)' }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((invoice, index) => (
                  <tr 
                    key={invoice.id}
                    style={{ 
                      backgroundColor: index % 2 === 0 ? 'var(--white)' : 'var(--gray-light)',
                      borderBottom: '1px solid var(--gray-medium)'
                    }}
                  >
                    <td style={{ padding: '12px', fontWeight: '500' }}>
                      {invoice.invoiceNumber}
                    </td>
                    <td style={{ padding: '12px' }}>
                      <div style={{ fontWeight: '500' }}>{invoice.customerName}</div>
                      <div style={{ fontSize: '12px', color: 'var(--gray-dark)' }}>
                        {invoice.customerEmail}
                      </div>
                    </td>
                    <td style={{ padding: '12px' }}>
                      {invoice.customerGstNumber || 'B2C'}
                    </td>
                    <td style={{ padding: '12px' }}>
                      {formatDateForDisplay(invoice.invoiceDate)}
                    </td>
                    <td style={{ padding: '12px' }}>
                      <div>{invoice.description}</div>
                      <div style={{ fontSize: '11px', color: 'var(--primary-blue)', marginTop: '4px' }}>
                        Platform Subscription
                      </div>
                    </td>
                    <td style={{ padding: '12px', fontWeight: '500' }}>
                      ₹{invoice.totalAmount || invoice.amount}
                    </td>
                    <td style={{ padding: '12px' }}>
                      <span style={{
                        color: getInvoiceStatusColor(invoice.status),
                        fontWeight: '500'
                      }}>
                        {invoice.status}
                      </span>
                    </td>
                    <td style={{ padding: '12px' }}>
                      <div style={{ fontSize: '12px' }}>
                        {invoice.paymentDate && (
                          <div>Date: {formatDateForDisplay(invoice.paymentDate)}</div>
                        )}
                        {invoice.paymentMode && (
                          <div>Mode: {invoice.paymentMode}</div>
                        )}
                        {invoice.paidAmount && (
                          <div>Amount: ₹{invoice.paidAmount}</div>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      {invoice.hasPdf && (
                        <button 
                          className="btn btn-primary"
                          style={{ fontSize: '11px', padding: '4px 8px' }}
                          onClick={() => handleDownloadInvoice(invoice.id, invoice.pdfFileName || `subscription_invoice_${invoice.invoiceNumber}.pdf`)}
                          title="Download PDF"
                        >
                          📄 Download
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showGenerateInvoiceModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '620px', overflow: 'visible' }} role="dialog" aria-modal="true" aria-labelledby="generateSubscriptionInvoiceTitle">
            <div className="modal-header">
              <div>
                <h3 id="generateSubscriptionInvoiceTitle">Generate Subscription Invoice</h3>
                <div style={{ color: 'var(--gray-dark)', fontSize: '13px', marginTop: '4px' }}>
                  Recover an invoice from a verified subscription payment
                </div>
              </div>
              <button
                type="button"
                className="close-btn"
                onClick={closeGenerateInvoiceModal}
                disabled={invoiceGenerationLoading}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="profile-form">
              {invoiceGenerationError && <div className="alert alert-error">{invoiceGenerationError}</div>}

              <div className="form-group">
                <label htmlFor="subscriptionInvoiceMonth" className="form-label">Payment month</label>
                <input id="subscriptionInvoiceMonth" type="month" className="form-input"
                  value={invoiceMonth} disabled={invoiceGenerationLoading}
                  onChange={event => { setInvoiceMonth(event.target.value); setInvoiceGenerationError(''); }} />
                <small>Select the month of the successful payment. The invoice will retain its payment date.</small>
              </div>

              <div className="form-group" style={{ position: 'relative' }}>
                <label htmlFor="subscriptionInvoiceUserSearch" className="form-label">Search SYSTEM_EDITOR</label>
                <input
                  id="subscriptionInvoiceUserSearch"
                  type="search"
                  className="form-input"
                  value={invoiceUserSearch}
                  onChange={(event) => {
                    setInvoiceUserSearch(event.target.value);
                    setSelectedInvoiceUser(null);
                  }}
                  placeholder="Search by name or email"
                  autoComplete="off"
                  disabled={invoiceGenerationLoading}
                />

                {!selectedInvoiceUser && invoiceUserSearch.trim().length >= 2 && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    left: 0,
                    zIndex: 5,
                    maxHeight: '230px',
                    overflowY: 'auto',
                    background: 'var(--white)',
                    border: '1px solid var(--gray-medium)',
                    borderRadius: '6px',
                    boxShadow: '0 10px 24px rgba(44, 82, 130, 0.16)'
                  }}>
                    {invoiceUserSearchLoading ? (
                      <div style={{ padding: '14px', color: 'var(--gray-dark)' }}>Searching...</div>
                    ) : invoiceUserResults.length > 0 ? invoiceUserResults.map((result) => (
                      <button
                        key={result.id}
                        type="button"
                        onClick={() => selectInvoiceUser(result)}
                        style={{
                          width: '100%',
                          padding: '11px 14px',
                          background: 'var(--white)',
                          border: 0,
                          borderBottom: '1px solid var(--gray-medium)',
                          cursor: 'pointer',
                          textAlign: 'left'
                        }}
                      >
                        <strong style={{ display: 'block', color: 'var(--dark-blue)' }}>{result.fullName}</strong>
                        <span style={{ display: 'block', color: 'var(--gray-dark)', fontSize: '12px', marginTop: '3px' }}>{result.email}</span>
                      </button>
                    )) : (
                      <div style={{ padding: '14px', color: 'var(--gray-dark)' }}>No matching SYSTEM_EDITOR users found.</div>
                    )}
                  </div>
                )}
              </div>

              {selectedInvoiceUser && (
                <div style={{
                  padding: '16px',
                  background: 'var(--pale-blue)',
                  border: '1px solid var(--light-blue)',
                  borderRadius: '6px',
                  marginBottom: '18px'
                }}>
                  <strong style={{ display: 'block', color: 'var(--dark-blue)' }}>{selectedInvoiceUser.fullName}</strong>
                  <span style={{ display: 'block', color: 'var(--gray-dark)', fontSize: '13px', marginTop: '4px' }}>{selectedInvoiceUser.email}</span>
                </div>
              )}

              <div style={{ color: 'var(--gray-dark)', fontSize: '13px', lineHeight: '1.55' }}>
                The latest successful payment transaction created in the selected month will be used. Generation is blocked if this user already has a non-cancelled subscription invoice for that month.
              </div>

              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={closeGenerateInvoiceModal} disabled={invoiceGenerationLoading}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={generateMissingInvoice}
                  disabled={!selectedInvoiceUser || invoiceGenerationLoading}
                >
                  {invoiceGenerationLoading ? 'Generating...' : 'Generate Invoice'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSubscriptionInvoices;
